import EmailOutlined from '@mui/icons-material/EmailOutlined';
import { Box, FormHelperText } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { LANG_TEXT } from '../../../../constants/language';
import { FoodTruckReminderState } from '../../PreorderRules.types';
import CardLayout from '../card/CardLayout';
import { useStyles } from './styles';

interface AstrolabReminderProps {
  onChange?: (values: FoodTruckReminderState & { isValid: boolean }) => void;
  initialValues: FoodTruckReminderState;
  resetKey?: number;
}

export default function AstrolabReminder({ onChange, initialValues, resetKey = 0 }: AstrolabReminderProps) {
  const classes = useStyles();
  const { t } = useTranslation();

  const [emailStr, setEmailStr] = useState(initialValues.emails?.join(', ') || '');
  const [error, setError] = useState<string>('');

  const validateEmails = useCallback((emailString: string): { isValid: boolean; error: string } => {
    if (!emailString.trim()) {
      return { isValid: true, error: '' };
    }

    const emails = emailString.split(',').map((email) => email.trim()).filter((email) => email.length > 0);

    if (emails.length > 3) {
      return { isValid: false, error: t(LANG_TEXT.MAX_EMAILS_EXCEEDED) };
    }

    const emailSchema = yup.string().email();
    for (const email of emails) {
      if (!emailSchema.isValidSync(email)) {
        return { isValid: false, error: t(LANG_TEXT.INVALID_EMAIL) };
      }
    }

    return { isValid: true, error: '' };
  }, [t]);

  useEffect(() => {
    const newEmailStr = initialValues.emails?.join(', ') || '';
    setEmailStr(newEmailStr);
    
    const validation = validateEmails(newEmailStr);
    setError(validation.error);
    
    onChange?.({
      emails: newEmailStr.split(',').map((email) => email.trim()).filter((email) => email.length > 0),
      isValid: validation.isValid,
    });
  }, [resetKey]);

  const handleEmailChange = useCallback((value: string) => {
    setEmailStr(value);
    
    const validation = validateEmails(value);
    setError(validation.error);
    
    onChange?.({
      emails: value.split(',').map((email) => email.trim()).filter((email) => email.length > 0),
      isValid: validation.isValid,
    });
  }, [onChange, validateEmails]);

  const labelContent = (
    <div className={classes.labelColumn}>
      <div className={classes.labelSection}>
        <div className={classes.label}>{t(LANG_TEXT.EMAIL_ADDRESS)}</div>
      </div>
    </div>
  );

  const actionContent = (
    <Box display="flex" flexDirection="column" gap={3}>
      {/* Email Input */}
      <Box display="flex" alignItems="center" gap={1} position="relative">
        <Box position="absolute" left="12px" display="flex" alignItems="center" sx={{ pointerEvents: 'none', color: '#667085' }}>
          <EmailOutlined sx={{ fontSize: 20 }} />
        </Box>
        <input
          key={`email-${resetKey}`}
          type="email"
          placeholder={t(LANG_TEXT.EMAIL_REQUIRED)}
          value={emailStr}
          onChange={(e) => handleEmailChange(e.target.value)}
          style={{
            width: '100%',
            height: '40px',
            padding: '8px 12px 8px 40px',
            borderRadius: '8px',
            border: error ? '1px solid #F04438' : '1px solid #D0D5DD',
            background: '#FFF',
            fontSize: '14px',
            color: '#101828',
            boxShadow: '0 1px 2px 0 rgba(16, 24, 40, 0.05)',
          }}
        />
      </Box>
      {error && (
        <FormHelperText error sx={{ margin: '3px 14px 0px' }}>
          {error}
        </FormHelperText>
      )}
    </Box>
  );

  return <CardLayout title={t(LANG_TEXT.ASTROLAB_REMINDERS)} label={labelContent} action={actionContent} />;
}
